import { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';
import { Input } from './ui/input';

/**
 * CurrencyInput - A formatted currency input component
 * Displays values with thousand separators and handles numeric input
 * 
 * @param {string} value - The numeric value (as string)
 * @param {function} onChange - Callback with raw numeric value
 * @param {string} placeholder - Input placeholder
 * @param {string} className - Additional CSS classes
 * @param {boolean} required - Whether field is required
 * @param {string} dataTestId - Test ID for the input
 */
export function CurrencyInput({ 
  value, 
  onChange, 
  placeholder = "0", 
  className = "",
  required = false,
  dataTestId,
  ...props 
}) {
  const [displayValue, setDisplayValue] = useState('');
  
  // Format number with thousand separators
  const formatNumber = (num) => {
    if (!num && num !== 0) return '';
    const number = parseFloat(num);
    if (isNaN(number)) return '';
    return number.toLocaleString('en-AU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };
  
  // Parse formatted string back to number
  const parseNumber = (str) => {
    if (!str) return '';
    // Remove all non-numeric characters except decimal point
    const cleaned = str.replace(/[^0-9.]/g, '');
    return cleaned;
  };
  
  // Update display value when external value changes
  useEffect(() => {
    setDisplayValue(formatNumber(value));
  }, [value]);
  
  const handleChange = (e) => {
    const rawValue = e.target.value;
    
    // Allow empty input
    if (!rawValue) {
      setDisplayValue('');
      onChange('');
      return;
    }
    
    // Parse the raw input
    const numericValue = parseNumber(rawValue);
    
    // Validate it's a valid number
    if (numericValue && !isNaN(parseFloat(numericValue))) {
      setDisplayValue(formatNumber(numericValue));
      onChange(numericValue);
    }
  };
  
  const handleBlur = () => {
    // Reformat on blur to ensure proper formatting
    if (value) {
      setDisplayValue(formatNumber(value));
    }
  };
  
  const handleFocus = (e) => {
    // Select all text on focus for easy replacement
    e.target.select();
  };

  return (
    <div className="relative">
      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
      <Input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={`pl-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)] ${className}`}
        required={required}
        data-testid={dataTestId}
        {...props}
      />
    </div>
  );
}

/**
 * PriceRangeInput - Dual currency inputs for min/max price ranges
 */
export function PriceRangeInput({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  minPlaceholder = "Min",
  maxPlaceholder = "Max",
  className = "",
  minTestId,
  maxTestId
}) {
  return (
    <div className={`flex gap-3 items-center ${className}`}>
      <div className="flex-1">
        <CurrencyInput
          value={minValue}
          onChange={onMinChange}
          placeholder={minPlaceholder}
          dataTestId={minTestId}
        />
      </div>
      <span className="text-[var(--theme-text-muted)]">to</span>
      <div className="flex-1">
        <CurrencyInput
          value={maxValue}
          onChange={onMaxChange}
          placeholder={maxPlaceholder}
          dataTestId={maxTestId}
        />
      </div>
    </div>
  );
}
